import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  MapPinIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { ParkingMap, COULEURS_STATUT_CARTE } from '@/components/carte/ParkingMap';
import { cartePostesService, type PosteCarte, type StatutCarte } from '@/services/cartePostesService';
import { parkingService } from '@/services/parkingService';
import { getErrorMessage } from '@/services/api';
import { useAuthorization } from '@/hooks/useAuthorization';
import { useIsGidRegul } from '@/store/authStore';
import type { StatutDeneignement } from '@/types';
import { STATUT_LABELS as STATUT_INTERVENTION_LABELS } from '@/types';

/**
 * Carte du statut hivernal des postes (SFD "Carte du statut hivernal des
 * postes"). Un seul appel API (cartePostesService.getCarte) renvoie tout ce
 * qu'affiche cette page, statut deja calcule cote serveur (RG-C01) - cette
 * page ne croise jamais elle-meme les parkings et les interventions.
 */

const AEROPORT_STORAGE_KEY = 'falcon-carte-aeroport';

type Aeroport = 'ORY' | 'CDG';

const STATUT_CARTE_LABELS: Record<StatutCarte, string> = {
  indisponible: 'Indisponible',
  en_cours: 'Deneigement en cours',
  en_attente_deneignement: 'En attente de deneigement',
  demande: 'Deneigement demande',
  effectue: 'Deneigement effectue',
  aucune: 'Aucune demande',
};

const INDICATEURS: { statut: StatutCarte; label: string }[] = [
  { statut: 'demande', label: 'Deneigement demande' },
  { statut: 'en_attente_deneignement', label: 'En attente de deneigement' },
  { statut: 'en_cours', label: 'Deneigement en cours' },
  { statut: 'effectue', label: 'Deneigement effectue' },
];

function lireAeroportMemorise(): Aeroport {
  try {
    const valeur = localStorage.getItem(AEROPORT_STORAGE_KEY);
    return valeur === 'CDG' ? 'CDG' : 'ORY';
  } catch {
    return 'ORY';
  }
}

export function CartePostesPage() {
  const queryClient = useQueryClient();
  const { canAccess } = useAuthorization();
  const estGidRegul = useIsGidRegul();

  const [aeroport, setAeroport] = useState<Aeroport>(lireAeroportMemorise);
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [filtreStatut, setFiltreStatut] = useState<StatutCarte | null>(null);
  const [filtreAire, setFiltreAire] = useState<string | null>(null);
  const [filtreSituation, setFiltreSituation] = useState<'contact' | 'large' | 'sans_situation' | ''>('');
  const [nouveauStatutHiver, setNouveauStatutHiver] = useState('');

  const changerAeroport = (valeur: Aeroport) => {
    setAeroport(valeur);
    setSelectedCode(null);
    setFiltreStatut(null);
    setFiltreAire(null);
    try {
      localStorage.setItem(AEROPORT_STORAGE_KEY, valeur);
    } catch {
      // confort d'affichage uniquement - pas grave si localStorage est indisponible
    }
  };

  const { data, isLoading } = useQuery({
    queryKey: ['carte-postes', aeroport],
    queryFn: () => cartePostesService.getCarte(aeroport),
    refetchInterval: 30000,
  });

  const statutHiverMutation = useMutation({
    mutationFn: ({ id, statut }: { id: number; statut: 'disponible' | 'en_deneignement' | 'indisponible' }) =>
      parkingService.updateStatutHiver(id, statut),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['carte-postes'] });
      toast.success('Statut hiver mis a jour');
      setNouveauStatutHiver('');
    },
    onError: (error: unknown) => {
      // RG-C06 : message dedie sur le refus 409 (intervention encore active).
      toast.error(getErrorMessage(error) || 'Une intervention est encore active sur ce poste.');
    },
  });

  const postes = data?.postes ?? [];

  const postesFiltres = useMemo(() => {
    return postes.filter((p) => {
      if (filtreStatut && p.statutCarte !== filtreStatut) return false;
      if (filtreAire && p.aire !== filtreAire) return false;
      if (filtreSituation === 'sans_situation' && p.situation !== null) return false;
      if ((filtreSituation === 'contact' || filtreSituation === 'large') && p.situation !== filtreSituation) return false;
      return true;
    });
  }, [postes, filtreStatut, filtreAire, filtreSituation]);

  const aires = useMemo(
    () => Array.from(new Set(postes.map((p) => p.aire))).sort(),
    [postes]
  );

  const avancementParAire = useMemo(() => {
    const parAire: Record<string, { demandes: number; attente: number; cours: number; effectue: number; total: number }> = {};
    for (const poste of postes) {
      parAire[poste.aire] ??= { demandes: 0, attente: 0, cours: 0, effectue: 0, total: 0 };
      parAire[poste.aire].total++;
      if (poste.statutCarte === 'demande') parAire[poste.aire].demandes++;
      if (poste.statutCarte === 'en_attente_deneignement') parAire[poste.aire].attente++;
      if (poste.statutCarte === 'en_cours') parAire[poste.aire].cours++;
      if (poste.statutCarte === 'effectue') parAire[poste.aire].effectue++;
    }
    return Object.entries(parAire).sort(([a], [b]) => a.localeCompare(b));
  }, [postes]);

  const posteSelectionne = postes.find((p) => p.code === selectedCode) ?? null;

  const toggleFiltreStatut = (statut: StatutCarte) => {
    setFiltreStatut((courant) => (courant === statut ? null : statut));
  };

  const totalAffiche = postes.length;
  const pct = (n: number) => (totalAffiche > 0 ? Math.round((n / totalAffiche) * 100) : 0);

  return (
    <div className="space-y-4">
      {/* En-tete */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MapPinIcon className="w-6 h-6" />
            Statut hivernal des postes
          </h2>
          <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
            {(['ORY', 'CDG'] as const).map((code) => (
              <button
                key={code}
                type="button"
                onClick={() => changerAeroport(code)}
                className={`px-4 py-1.5 text-sm font-medium ${
                  aeroport === code ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {code === 'ORY' ? 'Paris-Orly (ORY)' : 'Paris-CDG (CDG)'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {isLoading || !data ? (
        <div className="flex flex-col items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
          <p className="text-sm text-gray-500 mt-3">Chargement de la carte...</p>
        </div>
      ) : (
        <>
          {/* Indicateurs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {INDICATEURS.map(({ statut, label }) => (
              <button
                key={statut}
                type="button"
                onClick={() => toggleFiltreStatut(statut)}
                className={`card text-left transition-shadow ${filtreStatut === statut ? 'ring-2 ring-primary-500' : 'hover:shadow-md'}`}
              >
                <div className="card-body">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: COULEURS_STATUT_CARTE[statut] }}
                    />
                    <p className="text-xs text-gray-500 uppercase">{label}</p>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{data.compteurs[statut]}</p>
                  {statut === 'demande' && (
                    <p className="text-xs text-gray-400">{pct(data.compteurs.demande)}% des postes demandes</p>
                  )}
                </div>
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-6 gap-4">
            {/* Carte + filtres + legende */}
            <div className="xl:col-span-5 space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <select
                  value={filtreStatut ?? ''}
                  onChange={(e) => setFiltreStatut((e.target.value || null) as StatutCarte | null)}
                  className="input w-auto text-sm"
                >
                  <option value="">Statut : tous</option>
                  {Object.entries(STATUT_CARTE_LABELS).map(([valeur, label]) => (
                    <option key={valeur} value={valeur}>{label}</option>
                  ))}
                </select>
                <select
                  value={filtreAire ?? ''}
                  onChange={(e) => setFiltreAire(e.target.value || null)}
                  className="input w-auto text-sm"
                >
                  <option value="">Aire : toutes</option>
                  {aires.map((aire) => (
                    <option key={aire} value={aire}>Aire {aire}</option>
                  ))}
                </select>
                <select
                  value={filtreSituation}
                  onChange={(e) => setFiltreSituation(e.target.value as typeof filtreSituation)}
                  className="input w-auto text-sm"
                >
                  <option value="">Situation : toutes</option>
                  <option value="contact">Contact</option>
                  <option value="large">Large</option>
                  <option value="sans_situation">Non renseignee</option>
                </select>
                {(filtreStatut || filtreAire || filtreSituation) && (
                  <button
                    type="button"
                    onClick={() => { setFiltreStatut(null); setFiltreAire(null); setFiltreSituation(''); }}
                    className="text-xs text-primary-600 hover:underline"
                  >
                    Reinitialiser les filtres
                  </button>
                )}
                <span className="text-xs text-gray-400 ml-auto">
                  {postesFiltres.length} / {totalAffiche} poste(s) affiche(s)
                </span>
              </div>

              <ParkingMap
                postes={postesFiltres}
                aeroport={aeroport}
                selectedCode={selectedCode}
                onSelect={(p) => setSelectedCode(p.code)}
                zoomKey={filtreAire ?? 'tous'}
                hauteur={window.innerWidth < 768 ? 420 : 'calc(100vh - 280px)'}
              />

              {/* Legende */}
              <div className="card">
                <div className="card-body flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
                  {(Object.keys(STATUT_CARTE_LABELS) as StatutCarte[]).map((statut) => (
                    <div key={statut} className="flex items-center gap-1.5">
                      <span
                        className="w-3 h-3 rounded-full border border-gray-300 flex-shrink-0"
                        style={{ backgroundColor: COULEURS_STATUT_CARTE[statut] }}
                      />
                      <span className="text-gray-600">{STATUT_CARTE_LABELS[statut]}</span>
                      <span className="text-gray-400">{data.compteurs[statut]}</span>
                    </div>
                  ))}
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-full border border-dashed border-gray-500 flex-shrink-0" />
                    <span className="text-gray-600">Sans situation</span>
                    <span className="text-gray-400">{data.compteurs.sansSituation}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Panneau de droite : fiche / avancement / sans situation */}
            <div className="space-y-4">
              {posteSelectionne ? (
                <div className="card">
                  <div className="card-header flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold">{posteSelectionne.code}</h3>
                      <p className="text-xs text-gray-500">Aire {posteSelectionne.aire} - {aeroport}</p>
                    </div>
                    <button onClick={() => setSelectedCode(null)} className="text-gray-400 hover:text-gray-600">
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="card-body space-y-3 text-sm">
                    <div className="flex items-center gap-2">
                      <span
                        className="w-3 h-3 rounded-full border border-gray-300"
                        style={{ backgroundColor: COULEURS_STATUT_CARTE[posteSelectionne.statutCarte] }}
                      />
                      <span className="font-medium">{STATUT_CARTE_LABELS[posteSelectionne.statutCarte]}</span>
                    </div>

                    <div>
                      <span className="text-gray-500">Situation : </span>
                      {posteSelectionne.situation ? (
                        <span className="capitalize">{posteSelectionne.situation}</span>
                      ) : (
                        <span className="text-amber-600">Non renseignee</span>
                      )}
                    </div>

                    {posteSelectionne.intervention ? (
                      <>
                        <div className="border-t pt-3 space-y-1">
                          <p>
                            <span className="text-gray-500">Intervention : </span>
                            <span className="font-medium">{posteSelectionne.intervention.reference}</span>
                          </p>
                          <p>
                            <span className="text-gray-500">Statut : </span>
                            {STATUT_INTERVENTION_LABELS[posteSelectionne.intervention.statut as StatutDeneignement] ?? posteSelectionne.intervention.statut}
                          </p>
                          {posteSelectionne.intervention.equipe && (
                            <p><span className="text-gray-500">Equipe : </span>{posteSelectionne.intervention.equipe}</p>
                          )}
                          {posteSelectionne.intervention.heurePlanifiee && (
                            <p className="flex items-center gap-1">
                              <ClockIcon className="w-3.5 h-3.5 text-gray-400" />
                              {posteSelectionne.intervention.heurePlanifiee}
                            </p>
                          )}
                          <p>
                            <span className="text-gray-500">Occupation : </span>
                            {posteSelectionne.intervention.posteOccupe ? `Occupe - ${posteSelectionne.intervention.immatriculationAvion ?? ''}` : 'Libre'}
                          </p>
                          {posteSelectionne.intervention.jalonsTotal !== null && (
                            <p>
                              <span className="text-gray-500">Jalons : </span>
                              {posteSelectionne.intervention.jalonsValides} / {posteSelectionne.intervention.jalonsTotal}
                            </p>
                          )}
                        </div>
                        <Link
                          to={`/deneignements/${posteSelectionne.intervention.id}`}
                          className="btn-primary w-full text-center block"
                        >
                          Voir l'intervention
                        </Link>
                      </>
                    ) : (
                      canAccess.creationDeneignement && (
                        <Link
                          to={`/deneignements/nouveau?parking=${posteSelectionne.id}`}
                          className="btn-primary w-full text-center block"
                        >
                          Creer une demande pour ce poste
                        </Link>
                      )
                    )}

                    {estGidRegul && (
                      <div className="border-t pt-3 space-y-2">
                        <p className="text-xs text-gray-500 uppercase">Statut hiver - GID</p>
                        <div className="flex gap-2">
                          <select
                            value={nouveauStatutHiver}
                            onChange={(e) => setNouveauStatutHiver(e.target.value)}
                            className="input text-sm flex-1"
                          >
                            <option value="">Choisir...</option>
                            <option value="disponible">Disponible</option>
                            <option value="en_deneignement">En deneignement</option>
                            <option value="indisponible">Indisponible</option>
                          </select>
                          <button
                            type="button"
                            disabled={!nouveauStatutHiver || statutHiverMutation.isPending}
                            onClick={() => statutHiverMutation.mutate({
                              id: posteSelectionne.id,
                              statut: nouveauStatutHiver as 'disponible' | 'en_deneignement' | 'indisponible',
                            })}
                            className="btn-secondary text-sm"
                          >
                            Changer
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="card">
                  <div className="card-body text-center text-sm text-gray-400 py-8">
                    Cliquez sur un poste pour voir sa fiche
                  </div>
                </div>
              )}

              {/* Avancement par aire */}
              <div className="card">
                <div className="card-header">
                  <h3 className="font-semibold text-sm">Avancement par aire</h3>
                </div>
                <div className="card-body p-0">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-gray-400 border-b">
                        <th className="text-left px-3 py-1.5">Aire</th>
                        <th className="text-right px-2 py-1.5">Demandes</th>
                        <th className="text-right px-2 py-1.5">Attente</th>
                        <th className="text-right px-2 py-1.5">Cours</th>
                        <th className="text-right px-3 py-1.5">Effectue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {avancementParAire.map(([aire, stats]) => (
                        <tr
                          key={aire}
                          className="border-b last:border-0 hover:bg-gray-50 cursor-pointer"
                          onClick={() => setFiltreAire((courante) => (courante === aire ? null : aire))}
                        >
                          <td className="px-3 py-1.5 font-medium">{aire}</td>
                          <td className="text-right px-2 py-1.5">{stats.demandes}/{stats.total}</td>
                          <td className="text-right px-2 py-1.5">{stats.attente}</td>
                          <td className="text-right px-2 py-1.5">{stats.cours}</td>
                          <td className="text-right px-3 py-1.5">{stats.effectue}/{stats.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Encart sans situation - ROLE_ADMIN uniquement */}
              {canAccess.admin && data.compteurs.sansSituation > 0 && (
                <div className="card border-amber-300 bg-amber-50">
                  <div className="card-body text-sm">
                    <p className="flex items-center gap-2 text-amber-800 font-medium">
                      <ExclamationTriangleIcon className="w-4 h-4" />
                      {data.compteurs.sansSituation} poste(s) sans situation Contact/Large
                    </p>
                    <p className="text-amber-700 text-xs mt-1">
                      Aucune intervention ne peut y etre creee tant que la situation n'est pas renseignee (RG-01).
                    </p>
                    <Link to="/administration/parkings" className="text-xs text-amber-900 underline mt-2 inline-block">
                      Completer dans Administration &gt; Parkings
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default CartePostesPage;
